/**
 * Service de sécurité unifié
 * PHASE 2 REFACTORING: Centralisation de toute la logique de sécurité
 * Validation, audit, et protection contre les attaques
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/productionLogger';

export interface SecurityEvent {
  type: 'token_validation' | 'unauthorized_access' | 'suspicious_activity' | 'data_access';
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: {
    token?: string;
    userId?: string;
    ip?: string;
    userAgent?: string;
    resource?: string;
    action?: string;
    timestamp: string;
    [key: string]: any;
  };
}

export class SecurityService {
  private static readonly TOKEN_PATTERN = /^[a-zA-Z0-9\-_]+$/;
  private static readonly MAX_TOKEN_LENGTH = 100;
  private static readonly RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
  private static readonly MAX_REQUESTS_PER_WINDOW = 30;
  
  private static requestCounts = new Map<string, { count: number; resetTime: number }>();

  /**
   * Valide l'accès à une campagne via token
   * Remplace validate_campaign_access() PostgreSQL
   */
  static async validateCampaignAccess(token: string): Promise<boolean> {
    const startTime = Date.now();
    
    try {
      // 1. Validation basique du token
      if (!this.isValidTokenFormat(token)) {
        await this.auditSecurityEvent({
          type: 'token_validation',
          severity: 'medium',
          details: {
            token: this.sanitizeToken(token),
            reason: 'Invalid token format',
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 2. Rate limiting
      if (!this.checkRateLimit(token)) {
        await this.auditSecurityEvent({
          type: 'suspicious_activity',
          severity: 'high',
          details: {
            token: this.sanitizeToken(token),
            reason: 'Rate limit exceeded',
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 3. Vérification en base de données
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, status, recruiter_id')
        .eq('unique_link', token)
        .not('status', 'in', '("archived","deleted")')
        .single();

      if (error || !data) {
        await this.auditSecurityEvent({
          type: 'token_validation',
          severity: 'low',
          details: {
            token: this.sanitizeToken(token),
            reason: 'Token not found or campaign inactive',
            error: error?.message,
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 4. Log d'accès réussi
      await this.auditSecurityEvent({
        type: 'data_access',
        severity: 'low',
        details: {
          token: this.sanitizeToken(token),
          campaignId: data.id,
          recruiterId: data.recruiter_id,
          action: 'campaign_access_granted',
          duration: `${Date.now() - startTime}ms`,
          timestamp: new Date().toISOString()
        }
      });

      return true;

    } catch (error: any) {
      await this.auditSecurityEvent({
        type: 'unauthorized_access',
        severity: 'high',
        details: {
          token: this.sanitizeToken(token),
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        }
      });
      return false;
    }
  }

  /**
   * Valide l'accès à un référent via token
   */
  static async validateReferentAccess(referentToken: string): Promise<boolean> {
    const startTime = Date.now();
    
    try {
      // 1. Validation basique du token
      if (!this.isValidTokenFormat(referentToken)) {
        await this.auditSecurityEvent({
          type: 'token_validation',
          severity: 'medium',
          details: {
            token: this.sanitizeToken(referentToken),
            reason: 'Invalid referent token format',
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 2. Rate limiting
      if (!this.checkRateLimit(referentToken)) {
        await this.auditSecurityEvent({
          type: 'suspicious_activity',
          severity: 'high',
          details: {
            token: this.sanitizeToken(referentToken),
            reason: 'Rate limit exceeded for referent access',
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 3. Vérification en base de données
      const { data, error } = await supabase
        .from('referents')
        .select(`
          id, 
          status, 
          campaign_id,
          campaigns!inner(id, status, recruiter_id)
        `)
        .eq('token', referentToken)
        .neq('status', 'archived')
        .not('campaigns.status', 'in', '("archived","deleted")')
        .single();

      if (error || !data) {
        await this.auditSecurityEvent({
          type: 'token_validation',
          severity: 'low',
          details: {
            token: this.sanitizeToken(referentToken),
            reason: 'Referent token not found or inactive',
            error: error?.message,
            timestamp: new Date().toISOString()
          }
        });
        return false;
      }

      // 4. Log d'accès réussi
      await this.auditSecurityEvent({
        type: 'data_access',
        severity: 'low',
        details: {
          token: this.sanitizeToken(referentToken),
          referentId: data.id,
          campaignId: data.campaign_id,
          action: 'referent_access_granted',
          duration: `${Date.now() - startTime}ms`,
          timestamp: new Date().toISOString()
        }
      });

      return true;

    } catch (error: any) {
      await this.auditSecurityEvent({
        type: 'unauthorized_access',
        severity: 'high',
        details: {
          token: this.sanitizeToken(referentToken),
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        }
      });
      return false;
    }
  }

  /**
   * Audit des événements de sécurité
   */
  static async auditSecurityEvent(event: SecurityEvent): Promise<void> {
    try {
      // Log local pour debugging
      logger.info('Security Event', {
        type: event.type,
        severity: event.severity,
        details: event.details
      });

      // Enregistrer dans le monitoring
      const { MonitoringService } = await import('./monitoringService');
      MonitoringService.recordSecurityEvent(event.severity, 
        `${event.type}: ${event.details.reason || 'Security event'}`);

      // En production, on pourrait envoyer vers un service d'audit externe
      // await this.sendToAuditService(event);

    } catch (_error) {
      // Ne jamais faire échouer l'application à cause de l'audit
      logger.error('Failed to audit security event', _error as Error, { event });
    }
  }

  /**
   * Validation du format des tokens
   */
  private static isValidTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }

    if (token.length > this.MAX_TOKEN_LENGTH) {
      return false;
    }

    return this.TOKEN_PATTERN.test(token);
  }

  /**
   * Rate limiting simple en mémoire
   */
  private static checkRateLimit(identifier: string): boolean {
    const now = Date.now();
    const key = this.sanitizeToken(identifier);
    
    const current = this.requestCounts.get(key);
    
    if (!current || now > current.resetTime) {
      // Nouvelle fenêtre ou première requête
      this.requestCounts.set(key, {
        count: 1,
        resetTime: now + this.RATE_LIMIT_WINDOW
      });
      return true;
    }

    if (current.count >= this.MAX_REQUESTS_PER_WINDOW) {
      return false;
    }

    current.count++;
    return true;
  }

  /**
   * Sanitize token pour les logs (garde seulement les premiers et derniers caractères)
   */
  private static sanitizeToken(token: string): string {
    if (!token || token.length < 8) {
      return '[INVALID_TOKEN]';
    }
    
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  }

  /**
   * Nettoyage périodique du cache de rate limiting
   */
  static cleanupRateLimitCache(): void {
    const now = Date.now();
    for (const [key, data] of this.requestCounts.entries()) {
      if (now > data.resetTime) {
        this.requestCounts.delete(key);
      }
    }
  }
}

// Nettoyage automatique du cache toutes les 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    SecurityService.cleanupRateLimitCache();
  }, 5 * 60 * 1000);
}

export default SecurityService;